/**
 * Pricing — price, sale price, currency, COD (TikTok), special price (Lazada).
 * Always visible.
 *
 * TikTok: Price and Sale Price are editable (instant API, no audit).
 * COD toggle is editable (audited via Partial Edit).
 */

import { useEffect } from "react";
import { Check, X } from "lucide-react";
import { formatPrice } from "@/features/channels/components/ListingStatusPill";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig, Divider, SettingsRow } from "./shared";
import { EditableSettingsRow } from "./EditableSettingsRow";
import {
  useTikTokEditStore,
  selectFieldValue,
} from "../stores/useTikTokEditStore";

export function MarketplacePricingWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "";
  const config = getChannelConfig(channel);
  const isTikTok = channel === "TikTok Shop";

  if (isTikTok) {
    return <TikTokPricingSection config={config} formData={formData} />;
  }

  // ── Lazada: read-only rows (unchanged) ────────────────────────

  const price = formData?.[config.price] as number | undefined;
  const currency = config.currency ? (formData?.[config.currency] as string | undefined) : undefined;
  const priceDisplay = price != null ? formatPrice(price, currency) : "\u2014";

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: "Price", value: priceDisplay },
  ];

  if (config.specialPrice) {
    const specialPrice = formData?.[config.specialPrice] as number | undefined;
    if (specialPrice != null && specialPrice > 0) {
      const fromTime = config.specialFromTime ? (formData?.[config.specialFromTime] as string) : null;
      const toTime = config.specialToTime ? (formData?.[config.specialToTime] as string) : null;
      let label = "Special Price";
      if (toTime) {
        const d = new Date(toTime);
        label = `Special Price (until ${d.toLocaleDateString("en", { month: "short", day: "numeric" })})`;
      } else if (fromTime) {
        const d = new Date(fromTime);
        label = `Special Price (from ${d.toLocaleDateString("en", { month: "short", day: "numeric" })})`;
      }
      rows.push({ label, value: formatPrice(specialPrice) });
    }
  }

  if (config.available) {
    const available = formData?.[config.available];
    rows.push({
      label: "Available",
      value: available ? (
        <Check className="h-5 w-5 text-green-600 inline" />
      ) : (
        <X className="h-5 w-5 text-red-500 inline" />
      ),
    });
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Pricing
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {rows.map((row, i) => (
          <div key={row.label}>
            {i > 0 && <Divider />}
            <SettingsRow label={row.label} value={row.value} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TikTok-specific section (extracted for hooks) ─────────────────

function TikTokPricingSection({
  config,
  formData,
}: {
  config: ReturnType<typeof getChannelConfig>;
  formData: Record<string, any>;
}) {
  const price = formData?.[config.price] as number | undefined;
  const currency = config.currency ? (formData?.[config.currency] as string | undefined) : undefined;
  const salePrice = config.salePrice ? (formData?.[config.salePrice] as number | undefined) : undefined;
  const currencySuffix = currency || "THB";

  // Sale price validation: must be < price or empty
  const currentPrice = useTikTokEditStore(selectFieldValue("price"));
  const currentSalePrice = useTikTokEditStore(selectFieldValue("sale_price"));
  const setValidationError = useTikTokEditStore((s) => s.setValidationError);
  const clearValidationError = useTikTokEditStore((s) => s.clearValidationError);

  useEffect(() => {
    const p = Number(currentPrice) || 0;
    const sp = Number(currentSalePrice) || 0;
    if (sp > 0 && sp >= p) {
      setValidationError("sale_price", "Sale price must be less than price");
    } else {
      clearValidationError("sale_price");
    }
  }, [currentPrice, currentSalePrice, setValidationError, clearValidationError]);

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4 mb-2">
        Pricing
      </h3>
      <div className="rounded-2xl overflow-hidden bg-card">
        {/* Price — editable (instant API) */}
        <EditableSettingsRow
          label="Price"
          value={price}
          fieldKey="price"
          editType="number"
          suffix={currencySuffix}
          placeholder="0"
        />
        <Divider />

        {/* Sale Price — editable, optional */}
        <EditableSettingsRow
          label="Sale Price"
          value={salePrice}
          fieldKey="sale_price"
          editType="number"
          suffix={currencySuffix}
          placeholder="No sale price"
        />

        {currency && (
          <>
            <Divider />
            <SettingsRow label="Currency" value={currency} />
          </>
        )}

        {config.isCodAllowed && (
          <>
            <Divider />
            <EditableSettingsRow
              label="COD Allowed"
              value={formData?.[config.isCodAllowed]}
              fieldKey="is_cod_allowed"
              editType="toggle"
            />
          </>
        )}
      </div>
    </div>
  );
}
